/*
 * Cassaforte — nueva bóveda.
 * Solo interfaz: el cifrado, la API y la estructura de la bóveda usan los
 * servicios originales de Psono (secret, datastore-password, crypto-library).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import cryptoLibrary from "../../services/crypto-library";
import datastorePasswordService from "../../services/datastore-password";
import helperService from "../../services/helper";
import secretService from "../../services/secret";
import { getStore } from "../../services/store";
import browserClientService from "../../services/browser-client";
import { VAULT_CSS } from "./styles";
import { LOGO_SVG } from "./logo";
import widgetService from "../../services/widget";
import { AccountPanel, IDLE_OPTIONS, RecoverySetup, SettingsPanel } from "./account";
import { ImportSheet } from "./import";
import { type Billing, BillingBanner, fetchBilling, Paywall } from "./billing";
import { collectTrash, TrashSheet } from "./trash";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyObj = Record<string, any>;
type Kind = "all" | "login" | "note" | "card" | "other";

const LOGIN_TYPES = ["website_password", "application_password"];
const kindOf = (type: string): Exclude<Kind, "all"> =>
	LOGIN_TYPES.includes(type) ? "login" : type === "note" ? "note" : type === "credit_card" ? "card" : "other";

const COLORS = ["#2F7A6E", "#9A6B1F", "#4A5E60", "#6B4F7A", "#8A4B3C", "#1F5F8B", "#3E6B2F"];
const colorFor = (s: string) => COLORS[[...(s || "?")].reduce((a, c) => a + c.charCodeAt(0), 0) % COLORS.length];

/* ---------- Generador (aleatoriedad criptográfica, sin sesgo) ---------- */
interface GenOpts { mode: "random" | "phrase"; len: number; upper: boolean; lower: boolean; digits: boolean; symbols: boolean; noAmbiguous: boolean; words: number }
const SETS: Record<string, string> = { upper: "ABCDEFGHIJKLMNOPQRSTUVWXYZ", lower: "abcdefghijklmnopqrstuvwxyz", digits: "0123456789", symbols: "!@#$%^&*-_=+?" };
const WORDS = ["amber","anchor","apex","arbor","atlas","beacon","birch","bolt","cedar","cipher","cobalt","comet","copper","coral","delta","ember","falcon","fjord","flint","forge","garnet","glacier","harbor","helix","indigo","ivory","jasper","kernel","lantern","lumen","marble","meadow","nimbus","onyx","orbit","pillar","prism","quartz","raven","ridge","saffron","sequoia","summit","tundra","vector","willow","zenith","zephyr"];
function rnd(n: number): number {
	const max = Math.floor(4294967296 / n) * n;
	const a = new Uint32Array(1);
	do { crypto.getRandomValues(a); } while (a[0] >= max);
	return a[0] % n;
}
function charPool(o: GenOpts) {
	const sets: string[] = [];
	(["upper", "lower", "digits", "symbols"] as const).forEach((k) => {
		if (o[k]) sets.push(o.noAmbiguous ? SETS[k].replace(/[Il1O0o]/g, "") : SETS[k]);
	});
	return { sets, all: sets.join("") };
}
function generate(o: GenOpts): string {
	if (o.mode === "phrase") {
		const w = Array.from({ length: o.words }, () => { const x = WORDS[rnd(WORDS.length)]; return x[0].toUpperCase() + x.slice(1); });
		w.splice(rnd(w.length + 1), 0, String(rnd(90) + 10));
		return w.join("-");
	}
	const { sets, all } = charPool(o);
	if (!all) return "";
	const out = sets.map((s) => s[rnd(s.length)]);
	while (out.length < o.len) out.push(all[rnd(all.length)]);
	for (let i = out.length - 1; i > 0; i--) { const j = rnd(i + 1); [out[i], out[j]] = [out[j], out[i]]; }
	return out.join("");
}
function strengthOf(pw: string): { lvl: number; bits: number } {
	let n = 0;
	if (/[a-z]/.test(pw)) n += 26; if (/[A-Z]/.test(pw)) n += 26; if (/\d/.test(pw)) n += 10; if (/[^A-Za-z0-9]/.test(pw)) n += 33;
	const bits = Math.round(pw.length * Math.log2(n || 1));
	return { bits, lvl: bits < 40 ? 1 : bits < 60 ? 2 : bits < 80 ? 3 : 4 };
}

/* ---------- Íconos ---------- */
const I = {
	trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v6M14 11v6"/></svg>,
	import: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12M7 10l5 5 5-5"/><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"/></svg>,
	vault: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="4" width="18" height="16" rx="3"/><circle cx="12" cy="12" r="3.2"/><path d="M12 8.8V7M15.2 12H17"/></svg>,
	shield: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 3 5 6v5c0 4.4 3 8.3 7 10 4-1.7 7-5.6 7-10V6z"/><path d="m9 12 2 2 4-4"/></svg>,
	gear: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="3"/><path d="M19 12a7 7 0 0 0-.1-1.2l2-1.5-2-3.4-2.3.9a7 7 0 0 0-2-1.2L14 3h-4l-.5 2.6a7 7 0 0 0-2 1.2l-2.4-.9-2 3.4 2 1.5a7 7 0 0 0 0 2.4l-2 1.5 2 3.4 2.4-.9a7 7 0 0 0 2 1.2L10 21h4l.5-2.6a7 7 0 0 0 2-1.2l2.4.9 2-3.4-2-1.5c.1-.4.1-.8.1-1.2z"/></svg>,
	classic: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 6h16M4 12h16M4 18h10"/></svg>,
	logout: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3M10 17l-5-5 5-5M5 12h11"/></svg>,
	search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>,
	plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>,
	copy: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="11" height="11" rx="2.5"/><path d="M5 15V6a2 2 0 0 1 2-2h8"/></svg>,
	user: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4"/><path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6"/></svg>,
	key: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="8" cy="15" r="4"/><path d="m11 12 8-8M16 7l2 2"/></svg>,
	eye: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></svg>,
	eyeOff: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3l18 18M10.6 5.1A10.8 10.8 0 0 1 12 5c6.5 0 10 7 10 7a17.6 17.6 0 0 1-3.2 4.2M6.6 6.6C3.9 8.4 2 12 2 12s3.5 7 10 7c1.9 0 3.6-.6 5-1.4M9.9 9.9a3 3 0 0 0 4.2 4.2"/></svg>,
	x: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M6 6l12 12M18 6 6 18"/></svg>,
	note: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"><path d="M6 3h9l4 4v14H6z"/><path d="M9 11h7M9 15h5"/></svg>,
	card: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"><rect x="3" y="5" width="18" height="14" rx="3"/><path d="M3 10h18"/></svg>,
	dice: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 12a8 8 0 0 1 14-5.3M20 12a8 8 0 0 1-14 5.3"/><path d="M18 3v4h-4M6 21v-4h4"/></svg>,
	open: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5"/></svg>,
};

const Glyph = ({ item }: { item: AnyObj }) => {
	const k = kindOf(item.type);
	if (k === "note") return <div className="cf-glyph cf-note">{I.note}</div>;
	if (k === "card") return <div className="cf-glyph cf-card">{I.card}</div>;
	return <div className="cf-glyph" style={{ background: colorFor(item.name) }}>{(item.name || "?").trim().charAt(0).toUpperCase()}</div>;
};

/* Aplana la bóveda (carpetas y elementos compartidos) en una lista */
function flatten(node: AnyObj | undefined, path: string[], out: AnyObj[]) {
	if (!node) return;
	(node.items || []).forEach((it: AnyObj) => { if (!it.deleted && it.secret_id) out.push({ ...it, _path: path }); });
	(node.folders || []).forEach((f: AnyObj) => { if (!f.deleted) flatten(f, [...path, f.name], out); });
}

const VaultView = () => {
	const { t } = useTranslation();
	const [items, setItems] = useState<AnyObj[]>([]);
	const [loading, setLoading] = useState(true);
	const [query, setQuery] = useState("");
	const [kind, setKind] = useState<Kind>("all");
	const [selected, setSelected] = useState<AnyObj | null>(null);
	const [secret, setSecret] = useState<AnyObj | null>(null);
	const [revealed, setRevealed] = useState<Record<string, boolean>>({});
	const [toastMsg, setToastMsg] = useState("");
	const [sheetOpen, setSheetOpen] = useState(false);
	const [editing, setEditing] = useState<AnyObj | null>(null);
	const [trashAsk, setTrashAsk] = useState(false);
	const [recoveryCreated, setRecoveryCreated] = useState<string | undefined>(undefined);
	const [recoveryPrompt, setRecoveryPrompt] = useState<"" | "intro" | "new">("");
	const [accountOpen, setAccountOpen] = useState(false);
	const [settingsOpen, setSettingsOpen] = useState(false);
	const [importOpen, setImportOpen] = useState(false);
	const [billing, setBilling] = useState<Billing | null>(null);
	const [paywallOpen, setPaywallOpen] = useState(false);
	const [trashOpen, setTrashOpen] = useState(false);
	const [trashItems, setTrashItems] = useState<AnyObj[]>([]);
	const [idleMinutes, setIdleMinutes] = useState<number>(() => Number(localStorage.getItem("cf_idle_minutes")) || 15);
	const searchRef = useRef<HTMLInputElement>(null);
	const clearDelay: number = getStore().getState().settingsDatastore.clipboardClearDelay || 0;

	const load = useCallback(() => {
		return datastorePasswordService.getPasswordDatastore().then((ds) => {
			const out: AnyObj[] = [];
			const root = ds as unknown as AnyObj | undefined;
			flatten(root, [], out);
			const trashOut: AnyObj[] = [];
			collectTrash(root, trashOut);
			trashOut.sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
			setTrashItems(trashOut);
			const rc: string | undefined = root && root.cf_recovery ? root.cf_recovery.created : undefined;
			setRecoveryCreated(rc);
			const im = Number(root && root.cf_settings ? root.cf_settings.idle_minutes : 0);
			if (IDLE_OPTIONS.includes(im)) { setIdleMinutes(im); localStorage.setItem("cf_idle_minutes", String(im)); }
			if (root && !rc && !sessionStorage.getItem("cf_recovery_later")) setRecoveryPrompt("intro");
			out.sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
			setItems(out);
			setLoading(false);
		}, () => setLoading(false));
	}, []);
	useEffect(() => { load(); }, [load]);

	// Suscripción: estado al entrar y al volver de Stripe Checkout
	const refreshBilling = useCallback(() => fetchBilling().then((b) => { setBilling(b); return b; }, () => { setBilling(null); return null; }), []);
	useEffect(() => {
		const back = /billing=(success|cancel)/.exec(window.location.hash);
		if (back) window.history.replaceState(null, "", window.location.pathname + "#!/");
		refreshBilling().then((b) => {
			if (!back || back[1] !== "success") return;
			let tries = 0;
			const poll = (cur: Billing | null) => {
				if (cur && cur.can_write) { toast(t("CF_BILL_WELCOME")); return; }
				if (++tries > 8) return;
				setTimeout(() => refreshBilling().then(poll), 2000);
			};
			poll(b);
		});
	}, [refreshBilling]); // eslint-disable-line react-hooks/exhaustive-deps
	useEffect(() => {
		if (loading || recoveryPrompt || !recoveryCreated || !billing) return;
		if (billing.status === "none" && !sessionStorage.getItem("cf_paywall_later")) setPaywallOpen(true);
	}, [billing, loading, recoveryPrompt, recoveryCreated]);
	const canWrite = !billing || billing.can_write;
	const needPlan = (fn: () => void) => () => { if (canWrite) fn(); else setPaywallOpen(true); };

	const toast = useCallback((m: string) => { setToastMsg(""); setTimeout(() => setToastMsg(m), 10); }, []);
	useEffect(() => { if (!toastMsg) return; const h = setTimeout(() => setToastMsg(""), 2800); return () => clearTimeout(h); }, [toastMsg]);

	const visible = useMemo(() => {
		const q = query.trim().toLowerCase();
		return items.filter((i) => (kind === "all" || kindOf(i.type) === kind) &&
			(!q || [i.name, i.description, i.urlfilter, ...(i._path || [])].filter(Boolean).join(" ").toLowerCase().includes(q)));
	}, [items, query, kind]);
	const counts = useMemo(() => {
		const c: Record<string, number> = { all: items.length, login: 0, note: 0, card: 0, other: 0 };
		items.forEach((i) => { c[kindOf(i.type)]++; });
		return c;
	}, [items]);

	const select = (it: AnyObj) => {
		setSelected(it); setSecret(null); setRevealed({});
		secretService.readSecret(it.secret_id, it.secret_key).then((s) => setSecret(s as unknown as AnyObj), () => toast(t("CF_ERROR_READ")));
	};
	const closeDetail = () => { setSelected(null); setSecret(null); setRevealed({}); setTrashAsk(false); };
	// Psono guarda en segundo plano: recargamos un momento después para quedar sincronizados
	const syncTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const syncSoon = () => {
		if (syncTimer.current) clearTimeout(syncTimer.current);
		syncTimer.current = setTimeout(() => { load(); }, 1500);
	};
	const moveToTrash = async () => {
		if (!selected) return;
		const name = selected.name;
		try {
			const ds = (await datastorePasswordService.getPasswordDatastore()) as unknown as AnyObj | undefined;
			if (!ds) throw new Error("no datastore");
			const moved = selected;
			await widgetService.markItemAsDeleted(ds as never, moved.path, "password");
			// Actualización inmediata en pantalla; luego se sincroniza con el servidor
			setItems((prev) => prev.filter((i) => i.id !== moved.id));
			setTrashItems((prev) => [...prev.filter((i) => i.id !== moved.id), { ...moved, deleted: true }]);
			closeDetail(); toast(t("CF_TRASHED", { name })); syncSoon();
		} catch (e) { setTrashAsk(false); toast(t("CF_ERROR_SAVE")); }
	};

	const clipMsg = (base: string) => clearDelay ? t("CF_COPIED_CLEARS", { what: base, seconds: clearDelay }) : t("CF_COPIED", { what: base });
	const copyWith = (fn: (i: never) => unknown, it: AnyObj, what: string) => {
		Promise.resolve(fn(it as never)).then(() => toast(clipMsg(what)), () => toast(t("CF_ERROR_COPY")));
	};
	const copyText = (text: string, what: string) => {
		browserClientService.copyToClipboard(() => Promise.resolve(text)).then(() => toast(clipMsg(what)), () => toast(t("CF_ERROR_COPY")));
	};

	// Atajos: "/" busca, "n" crea, Esc cierra el detalle
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			const typing = /INPUT|TEXTAREA|SELECT/.test((document.activeElement as HTMLElement)?.tagName || "");
			if (sheetOpen || editing || accountOpen || settingsOpen || importOpen || paywallOpen || trashOpen || recoveryPrompt) return;
			if (e.key === "Escape" && selected) { closeDetail(); return; }
			if (typing) return;
			if (e.key === "/") { e.preventDefault(); searchRef.current?.focus(); }
			if (e.key.toLowerCase() === "n") { e.preventDefault(); if (canWrite) setSheetOpen(true); else setPaywallOpen(true); }
		};
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [sheetOpen, editing, accountOpen, settingsOpen, importOpen, paywallOpen, trashOpen, recoveryPrompt, selected, canWrite]);

	// Cierre de sesión por inactividad
	useEffect(() => {
		let last = Date.now();
		const bump = () => { last = Date.now(); };
		const limit = idleMinutes * 60000;
		const check = () => { if (Date.now() - last >= limit) { window.location.href = "logout-success.html"; } };
		const evs = ["mousemove", "mousedown", "keydown", "touchstart", "wheel", "scroll"];
		evs.forEach((ev) => window.addEventListener(ev, bump, { passive: true, capture: true }));
		const onVis = () => { if (document.visibilityState === "visible") check(); };
		document.addEventListener("visibilitychange", onVis);
		const h = setInterval(check, 15000);
		return () => { evs.forEach((ev) => window.removeEventListener(ev, bump, { capture: true })); document.removeEventListener("visibilitychange", onVis); clearInterval(h); };
	}, [idleMinutes]);


	const subOf = (i: AnyObj) => {
		const k = kindOf(i.type);
		if (k === "card") return i.description ? t("CF_CARD_ENDING", { last4: String(i.description).slice(-4) }) : t("CREDIT_CARD");
		return i.description || i.urlfilter || (i._path || []).join(" / ") || "";
	};

	const quick = (it: AnyObj) => {
		const k = kindOf(it.type);
		if (k === "login") return (<>
			<button className="cf-qb" title={t("CF_COPY_USER")} aria-label={t("CF_COPY_USER")} onClick={(e) => { e.stopPropagation(); copyWith(secretService.copyUsername, it, t("USERNAME")); }}>{I.user}<span>{t("USERNAME")}</span></button>
			<button className="cf-qb" title={t("CF_COPY_PASSWORD")} aria-label={t("CF_COPY_PASSWORD")} onClick={(e) => { e.stopPropagation(); copyWith(secretService.copyPassword, it, t("PASSWORD")); }}>{I.key}<span>{t("PASSWORD")}</span></button>
		</>);
		if (k === "card") return <button className="cf-qb" onClick={(e) => { e.stopPropagation(); copyWith(secretService.copyCreditCardNumber, it, t("CF_CARD_NUMBER")); }}>{I.copy}<span>{t("CF_CARD_NUMBER")}</span></button>;
		if (k === "note") return <button className="cf-qb" onClick={(e) => { e.stopPropagation(); copyWith(secretService.copyNoteContent, it, t("NOTE")); }}>{I.copy}<span>{t("CF_COPY")}</span></button>;
		return null;
	};

	/* ---------- Detalle ---------- */
	const Field = ({ id, label, value, isSecret }: { id: string; label: string; value?: string; isSecret?: boolean }) => {
		if (!value) return null;
		const shown = !isSecret || revealed[id];
		return (
			<div className="cf-field">
				<div><label>{label}</label><div className={"cf-val" + (shown ? "" : " cf-secret")}>{shown ? value : "•".repeat(Math.min(14, value.length))}</div></div>
				<div className="cf-acts">
					{isSecret && <button className="cf-ib" aria-pressed={!!revealed[id]} title={revealed[id] ? t("CF_HIDE") : t("CF_SHOW")}
						onClick={() => { const on = !revealed[id]; setRevealed({ ...revealed, [id]: on }); if (on) setTimeout(() => setRevealed((r) => ({ ...r, [id]: false })), 30000); }}>
						{revealed[id] ? I.eyeOff : I.eye}</button>}
					<button className="cf-ib" title={t("CF_COPY")} aria-label={t("CF_COPY") + " " + label} onClick={() => copyText(value, label)}>{I.copy}</button>
				</div>
			</div>
		);
	};
	const detail = () => {
		if (!selected) return null;
		const k = kindOf(selected.type);
		const s = secret || {};
		const pw: string = s.website_password_password || s.application_password_password || "";
		const st = pw ? strengthOf(pw) : null;
		const url: string = s.website_password_url || "";
		return (
			<div className="cf-dwrap">
				<div className="cf-dtop">
					<Glyph item={selected} />
					<div><h2>{selected.name}</h2>{url ? <a href={url} target="_blank" rel="noopener noreferrer">{t("CF_OPEN")} {helperService.parseUrl(url).authority_without_www || url}</a> : <p>{(selected._path || []).join(" / ")}</p>}</div>
					<button className="cf-close" aria-label={t("CLOSE")} onClick={closeDetail}>{I.x}</button>
				</div>
				{!secret && <p className="cf-muted">{t("CF_DECRYPTING")}</p>}
				{secret && k === "login" && (<>
					<Field id="u" label={t("USERNAME")} value={s.website_password_username || s.application_password_username} />
					<Field id="p" label={t("PASSWORD")} value={pw} isSecret />
					{st && <><div className={"cf-strength cf-s" + st.lvl}><i /><i /><i /><i /></div><p className="cf-slabel">{t(["", "CF_WEAK", "CF_FAIR", "CF_STRONG", "CF_VERY_STRONG"][st.lvl])}</p></>}
					{s.website_password_totp_code && <div className="cf-field"><div><label>{t("CF_TOTP")}</label><div className="cf-val">{t("CF_TOTP_HINT")}</div></div><div className="cf-acts"><button className="cf-ib" onClick={() => copyWith(secretService.copyTotpToken, selected, t("CF_TOTP"))}>{I.copy}</button></div></div>}
					<Field id="url" label={t("CF_WEBSITE")} value={url} />
					{(s.website_password_notes || s.application_password_notes) && <div className="cf-notebody">{s.website_password_notes || s.application_password_notes}</div>}
				</>)}
				{secret && k === "card" && (<>
					<div className="cf-cardviz"><div className="cf-chipm" /><div className="cf-cnum">•••• •••• •••• {String(s.credit_card_number || "").slice(-4)}</div><div className="cf-crow"><span>{s.credit_card_name}</span><span>{s.credit_card_valid_through ? String(s.credit_card_valid_through).replace(/^(\d{2})(\d{2})$/, "$1/$2") : ""}</span></div></div>
					<Field id="n" label={t("CF_CARD_NUMBER")} value={String(s.credit_card_number || "").replace(/(.{4})/g, "$1 ").trim()} isSecret />
					<div className="cf-two"><Field id="e" label={t("CF_EXPIRES")} value={s.credit_card_valid_through ? String(s.credit_card_valid_through).replace(/^(\d{2})(\d{2})$/, "$1/$2") : ""} /><Field id="c" label="CVV" value={s.credit_card_cvc} isSecret /></div>
					<Field id="h" label={t("CF_HOLDER")} value={s.credit_card_name} />
				</>)}
				{secret && k === "note" && (<>
					<div className="cf-notebody">{String(s.note_notes || "")}</div>
					<button className="cf-qb cf-accent" onClick={() => copyText(String(s.note_notes || ""), t("NOTE"))}>{I.copy}<span>{t("CF_COPY_NOTE")}</span></button>
				</>)}
				{secret && k === "other" && <p className="cf-muted">{t("CF_OTHER_TYPE")}</p>}
				{secret && k !== "other" && !trashAsk && <div className="cf-dactions">
					<button className="cf-btn" onClick={needPlan(() => setEditing({ item: selected, secret }))}>{t("CF_EDIT")}</button>
					<button className="cf-btn cf-danger" onClick={() => setTrashAsk(true)}>{t("CF_TRASH")}</button>
				</div>}
				{trashAsk && <div className="cf-inlineconfirm" role="alertdialog"><p><b>{t("CF_TRASH_Q", { name: selected.name })}</b></p><p>{t("CF_TRASH_TXT")}</p>
					<div className="cf-row2"><button className="cf-ghost" autoFocus onClick={() => setTrashAsk(false)}>{t("CANCEL")}</button><button className="cf-leave" onClick={moveToTrash}>{t("CF_TRASH")}</button></div></div>}
			</div>
		);
	};

	/* ---------- Render ---------- */
	const rows = visible.map((it) => (
		<div key={it.id || it.secret_id} className="cf-row" role="option" tabIndex={0} aria-selected={selected?.secret_id === it.secret_id}
			onClick={() => select(it)} onKeyDown={(e) => { if (e.key === "Enter") select(it); }}>
			<Glyph item={it} />
			<div className="cf-meta"><strong>{it.name}</strong><span>{subOf(it)}</span></div>
			<div className="cf-quick">{quick(it)}</div>
		</div>
	));

	return (
		<div className="cf-root">
			<style>{VAULT_CSS}</style>
			<div className="cf-app">
				<nav className="cf-rail" aria-label={t("CF_SECTIONS")}>
					<button aria-current="page" title={t("DATASTORE")}>{I.vault}<span className="cf-tip">{t("DATASTORE")}</span></button>
					<button onClick={() => setTrashOpen(true)} title={t("CF_TRASH_TITLE")}>{I.trash}{trashItems.length > 0 && <span className="cf-badge">{trashItems.length}</span>}<span className="cf-tip">{t("CF_TRASH_TITLE")}</span></button>
					<button onClick={needPlan(() => setImportOpen(true))} title={t("CF_IMP_TITLE")}>{I.import}<span className="cf-tip">{t("CF_IMP_TITLE")}</span></button>
					<button onClick={() => setAccountOpen(true)} title={t("CF_ACCOUNT")}>{I.user}<span className="cf-tip">{t("CF_ACCOUNT")}</span></button>
					<span className="cf-spacer" />
					<button onClick={() => setSettingsOpen(true)} title={t("SETTINGS")}>{I.gear}<span className="cf-tip">{t("SETTINGS")}</span></button>
					<button onClick={() => { window.location.href = "logout-success.html"; }} title={t("LOGOUT")}>{I.logout}<span className="cf-tip">{t("LOGOUT")}</span></button>
				</nav>

				<div className={"cf-main" + (selected ? " cf-open" : "")}>
					<section className="cf-col">
						<div className="cf-head">
							<div><h1 className="cf-h1"><span className="cf-logowrap" dangerouslySetInnerHTML={{ __html: LOGO_SVG }} /></h1><p className="cf-sub">{t("CF_COUNT", { count: items.length })}</p></div>
							<button className="cf-new" onClick={needPlan(() => setSheetOpen(true))}>{I.plus}<span>{t("CF_NEW")}</span><kbd>N</kbd></button>
						</div>
						<BillingBanner billing={billing} onChoose={() => setPaywallOpen(true)} toast={toast} />
						<div className="cf-search" role="search">
							<span className="cf-glass">{I.search}</span>
							<input ref={searchRef} type="search" value={query} placeholder={t("CF_SEARCH_PLACEHOLDER")} aria-label={t("CF_SEARCH_PLACEHOLDER")} autoComplete="off"
								onChange={(e) => setQuery(e.target.value)}
								onKeyDown={(e) => { if (e.key === "Enter" && visible[0]) select(visible[0]); if (e.key === "Escape") setQuery(""); }} />
							<kbd>/</kbd>
						</div>
						<div className="cf-chips" role="group">
							{(["all", "login", "note", "card", "other"] as Kind[]).filter((k) => k === "all" || counts[k] > 0).map((k) => (
								<button key={k} className="cf-chip" aria-pressed={kind === k} onClick={() => setKind(k)}>
									{t({ all: "CF_ALL", login: "CF_PASSWORDS", note: "CF_NOTES", card: "CF_CARDS", other: "CF_OTHERS" }[k])} <b>{counts[k]}</b>
								</button>))}
						</div>
						<div className="cf-list" role="listbox">
							{loading && <div className="cf-empty"><p>{t("CF_DECRYPTING")}</p></div>}
							{!loading && items.length === 0 && (
								<div className="cf-empty"><h3>{t("DATASTORE_EMPTY_TITLE")}</h3><p>{t("CF_EMPTY_HINT")}</p>
									<div className="cf-emptyactions"><button className="cf-new" onClick={needPlan(() => setSheetOpen(true))}>{I.plus}<span>{t("CF_SAVE_FIRST")}</span></button>
									<button className="cf-btn" onClick={needPlan(() => setImportOpen(true))}>{t("CF_IMP_FROM_OTHER")}</button></div></div>)}
							{!loading && items.length > 0 && visible.length === 0 && (
								<div className="cf-empty"><h3>{t("CF_NO_MATCH", { q: query })}</h3><p>{t("CF_NO_MATCH_HINT")}</p></div>)}
							{rows}
						</div>
					</section>
					<aside className="cf-detail" aria-label={t("CF_DETAIL")}>{detail()}</aside>
				</div>
			</div>
			{sheetOpen && <NewItemSheet onClose={() => setSheetOpen(false)} onSaved={(name) => { setSheetOpen(false); load(); toast(t("CF_SAVED", { name })); }} />}
			{editing && <NewItemSheet edit={editing} onClose={() => setEditing(null)} onSaved={(name) => {
				const it = editing.item; setEditing(null); closeDetail();
				load().then(() => select({ ...it, name })); toast(t("CF_UPDATED", { name }));
			}} />}
			{importOpen && <ImportSheet onClose={() => setImportOpen(false)} onDone={() => { load(); }} />}
			{settingsOpen && <SettingsPanel idleMinutes={idleMinutes} toast={toast} onClose={() => setSettingsOpen(false)}
				onIdleChange={(m) => { setIdleMinutes(m); localStorage.setItem("cf_idle_minutes", String(m)); }} />}
			{accountOpen && !recoveryPrompt && !paywallOpen && <AccountPanel recoveryCreated={recoveryCreated} toast={toast} onClose={() => setAccountOpen(false)} onNewCode={() => setRecoveryPrompt("new")}
				billing={billing} onChoosePlan={() => setPaywallOpen(true)} />}
			{paywallOpen && !recoveryPrompt && <Paywall billing={billing} toast={toast} onClose={() => { sessionStorage.setItem("cf_paywall_later", "1"); setPaywallOpen(false); }} />}
			{trashOpen && <TrashSheet items={trashItems} toast={toast} onClose={() => setTrashOpen(false)}
				onRestored={(it) => { setTrashItems((p) => p.filter((x) => x.id !== it.id)); setItems((p) => [...p.filter((x) => x.id !== it.id), { ...it, deleted: false, _path: it._path || [] }]); syncSoon(); }}
				onRemoved={(ids) => { setTrashItems((p) => p.filter((x) => !ids.includes(x.id))); syncSoon(); }} />}
			{recoveryPrompt && <RecoverySetup startGenerated={recoveryPrompt === "new"} toast={toast}
				onLater={recoveryPrompt === "intro" ? () => { sessionStorage.setItem("cf_recovery_later", "1"); setRecoveryPrompt(""); } : undefined}
				onDone={(created) => { setRecoveryCreated(created); setRecoveryPrompt(""); toast(t("CF_RECOVERY_SAVED")); }} />}
			<div className={"cf-toast" + (toastMsg ? " cf-show" : "")} role="status" aria-live="polite">
				<svg viewBox="0 0 30 30" aria-hidden="true"><circle cx="15" cy="15" r="12" fill="none" stroke="#E7ECE8" strokeWidth="2"/><circle cx="15" cy="15" r="5.5" fill="#D4A64A"/><path className="cf-hand" d="M15 15V10" stroke="#14282B" strokeWidth="2" strokeLinecap="round"/></svg>
				<span>{toastMsg}</span>
			</div>
		</div>
	);
};

/* ---------- Hoja de creación ---------- */
const META_KEYS = ["read_count", "create_date", "write_date", "callback_url", "callback_user", "callback_pass"];
function initialFields(edit?: AnyObj): AnyObj {
	if (!edit) return {};
	const s = edit.secret || {}; const k = kindOf(edit.item.type);
	if (k === "login") return { title: edit.item.name || "", url: s.website_password_url || "", user: s.website_password_username || s.application_password_username || "", pw: s.website_password_password || s.application_password_password || "" };
	if (k === "note") return { title: edit.item.name || "", body: s.note_notes || "" };
	const d = String(s.credit_card_number || "");
	const num = /^3[47]/.test(d) ? [d.slice(0, 4), d.slice(4, 10), d.slice(10)].filter(Boolean).join(" ") : d.replace(/(.{4})/g, "$1 ").trim();
	const e = String(s.credit_card_valid_through || "");
	return { title: edit.item.name || "", number: num, exp: e.length === 4 ? e.slice(0, 2) + "/" + e.slice(2) : e, cvv: s.credit_card_cvc || "", holder: s.credit_card_name || "" };
}
const NewItemSheet = ({ onClose, onSaved, edit }: { onClose: () => void; onSaved: (name: string) => void; edit?: AnyObj }) => {
	const { t } = useTranslation();
	const editKind = edit ? kindOf(edit.item.type) : null;
	const [type, setType] = useState<"login" | "note" | "card">(editKind === "note" ? "note" : editKind === "card" ? "card" : "login");
	const initial = useMemo(() => initialFields(edit), [edit]);
	const [f, setF] = useState<AnyObj>(initial);
	const [opts, setOpts] = useState<GenOpts>({ mode: "random", len: 20, upper: true, lower: true, digits: true, symbols: true, noAmbiguous: true, words: 5 });
	const [showOpts, setShowOpts] = useState(false);
	const [confirm, setConfirm] = useState(false);
	const [saving, setSaving] = useState(false);
	const set = (k: string, v: string) => setF((p) => ({ ...p, [k]: v }));
	const dirty = edit ? JSON.stringify(f) !== JSON.stringify(initial) : Object.values(f).some((v) => String(v || "").trim());
	const requestClose = () => (dirty ? setConfirm(true) : onClose());

	const digits = String(f.number || "").replace(/\D/g, "");
	const brand = /^4/.test(digits) ? "Visa" : /^(5[1-5]|2[2-7])/.test(digits) ? "Mastercard" : /^3[47]/.test(digits) ? "Amex" : "";
	const luhn = (d: string) => { let s = 0; [...d].reverse().forEach((c, i) => { let n = +c; if (i % 2) { n *= 2; if (n > 9) n -= 9; } s += n; }); return s % 10 === 0; };
	const maxLen = brand === "Amex" ? 15 : 16;
	const errNum = digits.length >= maxLen && !luhn(digits) ? t("CF_ERR_CARD") : "";
	const expD = String(f.exp || "").replace(/\D/g, "");
	let errExp = "";
	if (expD.length === 4) { const m = +expD.slice(0, 2), y = +expD.slice(2), now = new Date(), yy = now.getFullYear() % 100; if (y < yy || (y === yy && m < now.getMonth() + 1)) errExp = t("CF_ERR_EXPIRED"); }
	const pw = String(f.pw || "");
	const errPw = pw && pw.length < 8 ? t("CF_ERR_MIN8", { n: pw.length }) : "";
	const valid = String(f.title || "").trim() && !errNum && !errExp && (type !== "login" || pw.length >= 8);
	const st = pw ? strengthOf(pw) : null;

	const onExp = (v: string) => {
		const full = v.trim().match(/^(\d{1,2})\s*[/\-.]?\s*(\d{4})$/);   // 07/2028 o 7/2028 -> 0728
		const short = v.trim().match(/^(\d{1})\s*[/\-.]\s*(\d{2})$/);     // 7/28 -> 0728
		if (full) v = full[1].padStart(2, "0") + full[2].slice(2);
		else if (short) v = "0" + short[1] + short[2];
		let d = v.replace(/\D/g, "").slice(0, 4);
		if (d.length === 1 && +d > 1) d = "0" + d;
		if (d[0] === "1" && d.length >= 2 && +d[1] > 2) d = d[0] + d.slice(2);
		if (d.startsWith("00")) d = "0" + d.slice(2);
		set("exp", d.length > 2 ? d.slice(0, 2) + "/" + d.slice(2) : d);
	};
	const onNum = (v: string) => {
		const d = v.replace(/\D/g, ""); const amex = /^3[47]/.test(d); const x = d.slice(0, amex ? 15 : 16);
		set("number", amex ? [x.slice(0, 4), x.slice(4, 10), x.slice(10)].filter(Boolean).join(" ") : x.replace(/(.{4})/g, "$1 ").trim());
	};

	const save = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!valid || saving) return;
		setSaving(true);
		const title = String(f.title).trim();
		const item: AnyObj = edit ? { ...edit.item, name: title } : { id: cryptoLibrary.generateUuid(), name: title };
		delete item._path;
		const content: AnyObj = {};
		if (edit) { Object.keys(edit.secret || {}).forEach((key) => { if (!META_KEYS.includes(key)) content[key] = edit.secret[key]; }); }
		const keep = edit ? edit.item.type : "";
		if (type === "login" && keep === "application_password") {
			content.application_password_title = title;
			content.application_password_username = f.user || ""; if (f.user) item.description = f.user; else delete item.description;
			content.application_password_password = pw;
			item.password_hash = cryptoLibrary.sha1(pw).substring(0, 5).toLowerCase();
		} else if (type === "login") {
			item.type = "website_password";
			content.website_password_title = title;
			delete content.website_password_url; delete content.website_password_url_filter; delete item.urlfilter;
			if (f.url) {
				const url = /^[a-z]+:\/\//i.test(f.url) ? f.url : "https://" + f.url;
				const parsed = helperService.parseUrl(url);
				content.website_password_url = url;
				if (parsed.authority) { item.urlfilter = parsed.authority; content.website_password_url_filter = parsed.authority; }
			}
			if (f.user) { content.website_password_username = f.user; item.description = f.user; } else { delete content.website_password_username; delete item.description; }
			content.website_password_password = pw;
			item.password_hash = cryptoLibrary.sha1(pw).substring(0, 5).toLowerCase();
			if (!edit) { content.website_password_auto_submit = false; item.autosubmit = false; content.website_password_allow_http = false; item.allow_http = false; }
		} else if (type === "note") {
			item.type = "note"; content.note_title = title; content.note_notes = f.body || "";
		} else {
			item.type = "credit_card"; content.credit_card_title = title;
			content.credit_card_number = digits; if (digits) item.description = digits.replace(/.(?=.{4})/g, "x"); else delete item.description;
			content.credit_card_cvc = f.cvv || "";
			content.credit_card_name = String(f.holder || "").toUpperCase();
			content.credit_card_valid_through = expD;
		}
		try {
			const ds = (await datastorePasswordService.getPasswordDatastore()) as unknown as AnyObj | undefined;
			if (!ds) throw new Error("no datastore");
			if (edit) {
				await secretService.writeSecret(edit.item.secret_id, edit.item.secret_key, content as never);
				await widgetService.editItemSave(ds as never, item as never, edit.item.path, datastorePasswordService as never);
				onSaved(title);
				return;
			}
			const created = (await secretService.createSecret(content as never, item.id, ds.datastore_id, undefined)) as unknown as AnyObj | undefined;
			if (!created) throw new Error("create failed");
			item.secret_id = created.secret_id; item.secret_key = created.secret_key;
			if (!ds.items) ds.items = [];
			ds.items.push(item);
			await datastorePasswordService.saveDatastoreContent(ds as never, [[]]);
			datastorePasswordService.handleDatastoreContentChanged(ds as never);
			onSaved(title);
		} catch (err) {
			setSaving(false);
			alert(t("CF_ERROR_SAVE"));
		}
	};

	const TypeBtn = ({ k, icon, label }: { k: "login" | "note" | "card"; icon: React.ReactNode; label: string }) => (
		<button type="button" className="cf-type" role="radio" aria-checked={type === k} onClick={() => setType(k)}>{icon}{label}</button>);

	return (
		<div className="cf-scrim">
			<form className="cf-sheet" role="dialog" aria-modal="true" aria-labelledby="cfSheetTitle" onSubmit={save}>
				<div className="cf-shead">
					<button type="button" className="cf-sx" aria-label={t("CLOSE")} onClick={requestClose}>{I.x}</button>
					<h3 id="cfSheetTitle">{edit ? t("CF_EDITING", { name: edit.item.name }) : t("CF_SAVE_NEW")}</h3>
					<p className="cf-lead">{t("CF_ENCRYPTED_HERE")}</p>
					{!edit && <div className="cf-types" role="radiogroup">
						<TypeBtn k="login" icon={I.key} label={t("PASSWORD")} />
						<TypeBtn k="note" icon={I.note} label={t("NOTE")} />
						<TypeBtn k="card" icon={I.card} label={t("CF_CARD")} />
					</div>}
				</div>
				<div className="cf-sbody">
					<label className="cf-in"><span>{t("CF_TITLE")}</span><input autoFocus autoComplete="off" name="cf-title" value={f.title || ""} onChange={(e) => set("title", e.target.value)} placeholder={type === "card" ? "Visa" : type === "note" ? t("CF_NOTE_PH") : "Netflix"} /></label>
					{type === "login" && (<>
						<label className="cf-in"><span>{t("CF_WEBSITE_OR_APP")}</span><input autoComplete="off" name="cf-url" value={f.url || ""} onChange={(e) => set("url", e.target.value)} placeholder="netflix.com" /></label>
						<label className="cf-in"><span>{t("CF_USER_OR_EMAIL")}</span><input autoComplete="off" name="cf-user" value={f.user || ""} onChange={(e) => set("user", e.target.value)} placeholder="tu@correo.com" /></label>
						<label className="cf-in"><span>{t("PASSWORD")}</span>
							<div className="cf-pw"><input autoComplete="off" name="cf-secret" data-lpignore="true" value={pw} spellCheck={false} placeholder={t("CF_TYPE_PASSWORD")} onChange={(e) => set("pw", e.target.value)} />
								<div className="cf-pwbtns"><button type="button" className="cf-regen" onClick={() => set("pw", "")}>{t("CF_OWN")}</button>
									<button type="button" className="cf-regen" onClick={() => set("pw", generate(opts))}>{I.dice} {t("CF_GENERATE")}</button></div></div>
							<small className="cf-err">{errPw}</small></label>
						{st && <div className="cf-pwmeter"><div className={"cf-strength cf-s" + st.lvl}><i /><i /><i /><i /></div><span><b>{t(["", "CF_WEAK", "CF_FAIR", "CF_STRONG", "CF_VERY_STRONG"][st.lvl])}</b> ({st.bits} bits)</span></div>}
						<button type="button" className="cf-optlink" aria-expanded={showOpts} onClick={() => setShowOpts(!showOpts)}>{t("CF_GEN_OPTIONS")}</button>
						{showOpts && <div className="cf-opts">
							<div className="cf-seg">
								<button type="button" aria-pressed={opts.mode === "random"} onClick={() => setOpts({ ...opts, mode: "random" })}>{t("CF_RANDOM")}</button>
								<button type="button" aria-pressed={opts.mode === "phrase"} onClick={() => setOpts({ ...opts, mode: "phrase" })}>{t("CF_PASSPHRASE")}</button>
							</div>
							{opts.mode === "random" ? (<>
								<div className="cf-lenrow"><span>{t("CF_LENGTH")}</span><input type="range" min={8} max={64} value={opts.len} onChange={(e) => setOpts({ ...opts, len: +e.target.value })} /><output>{opts.len}</output></div>
								<div className="cf-toggles">
									{([["upper", "CF_UPPER"], ["lower", "CF_LOWER"], ["digits", "CF_DIGITS"], ["symbols", "CF_SYMBOLS"], ["noAmbiguous", "CF_NO_AMBIGUOUS"]] as const).map(([k, l]) => (
										<button key={k} type="button" className="cf-tg" aria-pressed={opts[k]} onClick={() => {
											const n = { ...opts, [k]: !opts[k] };
											if (k !== "noAmbiguous" && !n.upper && !n.lower && !n.digits && !n.symbols) return;
											setOpts(n);
										}}>{t(l)}</button>))}
								</div></>) : (
								<div className="cf-lenrow"><span>{t("CF_WORDS")}</span><input type="range" min={4} max={8} value={opts.words} onChange={(e) => setOpts({ ...opts, words: +e.target.value })} /><output>{opts.words}</output></div>)}
						</div>}
					</>)}
					{type === "note" && <label className="cf-in"><span>{t("CF_CONTENT")}</span><textarea autoComplete="off" rows={6} value={f.body || ""} onChange={(e) => set("body", e.target.value)} /></label>}
					{type === "card" && (<>
						<label className="cf-in"><span>{t("CF_CARD_NUMBER")}</span><div className="cf-rel"><input inputMode="numeric" name="cardnumber" autoComplete="cc-number" value={f.number || ""} onChange={(e) => onNum(e.target.value)} placeholder="0000 0000 0000 0000" /><span className="cf-brand">{brand}</span></div><small className="cf-err">{errNum}</small></label>
						<div className="cf-two">
							<label className="cf-in"><span>{t("CF_EXPIRES")}</span><input inputMode="numeric" name="cc-exp" autoComplete="cc-exp" maxLength={7} value={f.exp || ""} placeholder={t("CF_MMYY")} onChange={(e) => onExp(e.target.value)}
								onKeyDown={(e) => { if (e.key === "Backspace" && String(f.exp || "").endsWith("/")) { e.preventDefault(); set("exp", String(f.exp).slice(0, 2)); } }} /><small className="cf-err">{errExp}</small></label>
							<label className="cf-in"><span>CVV</span><input inputMode="numeric" name="cvc" autoComplete="cc-csc" maxLength={brand === "Amex" ? 4 : 3} value={f.cvv || ""} onChange={(e) => set("cvv", e.target.value.replace(/\D/g, ""))} placeholder="123" /></label>
						</div>
						<label className="cf-in"><span>{t("CF_HOLDER")}</span><input style={{ textTransform: "uppercase" }} name="ccname" autoComplete="cc-name" value={f.holder || ""} onChange={(e) => set("holder", e.target.value)} /></label>
					</>)}
				</div>
				<div className="cf-bar">
					<button type="button" className="cf-ghost" onClick={requestClose}>{t("CANCEL")}</button>
					<button type="submit" className="cf-save" disabled={!valid || saving}>{saving ? t("CF_SAVING") : edit ? t("CF_SAVE_CHANGES") : t("SAVE")}</button>
				</div>
				{confirm && <div className="cf-confirm" role="alertdialog"><div className="cf-box"><h4>{t("CF_LEAVE_Q")}</h4><p>{t("CF_LEAVE_TXT")}</p>
					<div className="cf-row2"><button type="button" className="cf-ghost" autoFocus onClick={() => setConfirm(false)}>{t("CF_KEEP_EDITING")}</button><button type="button" className="cf-leave" onClick={onClose}>{t("CF_LEAVE")}</button></div></div></div>}
			</form>
		</div>
	);
};

export default VaultView;
